import { memo, useEffect } from 'react';

import { atom, useAtomValue, useSetAtom, type ExtractAtomValue } from 'jotai';

import type { Unit } from '@core';
import { useToggle } from '@hooks';
import { useTranslate } from '@i18n';
import {
    activeData3dAtom,
    activeUserSettingsAtom,
    activityUnitAtom,
    axisFocusIdAtom,
    comparisonData3dAtom,
    currentFrameAtom,
    selectedAxisFocusIdAtom,
    selectedVisualizationAxesAtom,
    showVisualizationsAtom,
    syncedFrameAtom,
} from '@store';
import { Box, CaretDownIcon, CaretUpIcon, CompactParameterSelectionCard, ParameterSelectionCard, Text, u } from '@ui';

import * as css from './ElvaVisualizer.css';
import { getMeasurementValuesByName } from './helpers';
import {
    DISTANCE_MEASUREMENTS,
    VISUALIZATION_AXES,
    VISUALIZATION_AXES_PARENT_MAP,
    VISUALIZATION_MEASUREMENTS,
} from './mappings';
import { useMeasurementFocus } from './useMeasurementFocus';

export const CAMERA_TRANSITION_DURATION_MS = 500;
export const CAMERA_DISTANCE = 2;

export type ActiveData = ExtractAtomValue<typeof activeData3dAtom>;

const visualizationsAtom = atom((get) =>
    get(selectedVisualizationAxesAtom)
        .map((id) => VISUALIZATION_AXES?.[id])
        .filter(Boolean),
);

export const ElvaVisualizerMeasurements = memo(() => {
    const activityUnit = useAtomValue(activityUnitAtom);
    const activeData = useAtomValue(activeData3dAtom);
    const comparisonData = useAtomValue(comparisonData3dAtom);
    const currentFrame = useAtomValue(currentFrameAtom);
    const syncedFrame = useAtomValue(syncedFrameAtom);

    const showVisualizations = useAtomValue(showVisualizationsAtom);

    const selectedVisualizationAxes = useAtomValue(visualizationsAtom);

    const { currentFocusId, handleSetMeasurementFocus, resetFocus } = useMeasurementFocus({
        activeData,
        currentFrame,
    });

    useEffect(() => {
        if (showVisualizations) return;
        resetFocus();
    }, [resetFocus, showVisualizations]);

    if (!activeData || !showVisualizations) {
        return null;
    }
    return (
        <Box className={css.measurements} pointerEvents="none">
            {selectedVisualizationAxes.map((axis) => {
                const measurementId = VISUALIZATION_AXES_PARENT_MAP[axis?.id];
                const measurement = VISUALIZATION_MEASUREMENTS?.[measurementId];
                if (!measurement) return null;

                const values = getMeasurementValuesByName(activeData.measurements, measurementId, currentFrame);
                const comparisonValues = comparisonData
                    ? getMeasurementValuesByName(comparisonData.measurements, measurementId, syncedFrame)
                    : null;

                const value = values?.[axis.axisIndex];
                const comparisonValue = comparisonValues?.[axis.axisIndex];
                const keypointName = axis.targetPoints[0];
                return (
                    <Box pointerEvents="auto" key={axis.id}>
                        <ParameterSelectionCard
                            unit={axis.unit as Unit}
                            value={value}
                            comparisonValue={comparisonValue}
                            title={`${measurement.label} ${axis.label}`}
                            userPreferredUnit={activityUnit}
                            isSelected={currentFocusId === axis.id}
                            onClick={() => {
                                handleSetMeasurementFocus({
                                    axisId: axis.id,
                                    keypointName,
                                    measurementName: measurementId,
                                    axisIndex: axis.axisIndex,
                                    lock: axis.camera,
                                    targetPoints: axis.targetPoints ?? [],
                                });
                            }}
                        />
                    </Box>
                );
            })}
        </Box>
    );
});

export const VisualizerMeasurements = memo(() => {
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const activityUnit = useAtomValue(activityUnitAtom);
    const activeData = useAtomValue(activeData3dAtom);
    const comparisonData = useAtomValue(comparisonData3dAtom);
    const currentFrame = useAtomValue(currentFrameAtom);
    const syncedFrame = useAtomValue(syncedFrameAtom);

    const selectedVisualizationAxes = useAtomValue(visualizationsAtom);

    const setAxisFocusId = useSetAtom(axisFocusIdAtom);
    const selectedAxisFocusId = useAtomValue(selectedAxisFocusIdAtom);
    const t = useTranslate();

    const [expand, toggleExpand] = useToggle(true);

    if (!activeData) {
        return null;
    }

    return (
        <div className={css.measurements}>
            <Box display="flex" alignItems="center" alignSelf="end" gap={u(1)} onClick={toggleExpand}>
                <Text variant="h4" color="inverse">
                    {t('common.visualizations')}
                </Text>
                {expand ? (
                    <CaretUpIcon weight="bold" color="#fff" size={u(2)} width={u(2)} />
                ) : (
                    <CaretDownIcon weight="bold" color="#fff" size={u(2)} width={u(2)} />
                )}
            </Box>
            {expand && (
                <Box display="flex" flexDirection="column" gap={u(2)}>
                    {selectedVisualizationAxes.map((axis) => {
                        const measurementId = VISUALIZATION_AXES_PARENT_MAP[axis?.id];
                        const measurement = VISUALIZATION_MEASUREMENTS?.[measurementId];
                        if (!measurement) return null;

                        const values = getMeasurementValuesByName(activeData.measurements, measurementId, currentFrame);

                        const comparisonValues = comparisonData
                            ? getMeasurementValuesByName(comparisonData.measurements, measurementId, syncedFrame)
                            : null;

                        const value = values?.[axis.axisIndex];
                        const comparisonValue = userSettings.showComparison
                            ? comparisonValues?.[axis.axisIndex]
                            : undefined;

                        const noSelect = measurementId in DISTANCE_MEASUREMENTS;

                        return (
                            <CompactParameterSelectionCard
                                key={axis.id}
                                unit={axis.unit as Unit}
                                value={value}
                                comparisonValue={comparisonValue}
                                title={`${measurement.label} ${axis.label}`}
                                userPreferredUnit={activityUnit}
                                isSelected={selectedAxisFocusId === axis.id}
                                onClick={
                                    noSelect
                                        ? undefined
                                        : () => setAxisFocusId((prev) => (prev === axis.id ? undefined : axis.id))
                                }
                            />
                        );
                    })}
                </Box>
            )}
        </div>
    );
});
